import { lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider } from 'react-router-dom';

// EAGER LOADING PAGES
import { Auth0ProviderWithNavigate } from '@auth/auth0-provider-with-navigate';
import { AuthenticationGuard } from '@auth/AuthenticationGuard';
import { OrderPage } from '@features/admin/trading/components/orders/OrderPage';
import { AlertsPage } from '@pages/AlertsPage';
import { CalendarPage } from '@pages/CalendarPage';
import { CotPage } from '@pages/CotPage';
import { EndpointMeasures } from '@pages/EndpointMeasuresPage';
import { LiveWebsocketsPage } from '@pages/LiveWebsocketsPage';
import NotFoundPage from '@pages/NotFoundPage';
import { OCLTrading } from '@pages/ocl-trading/OCLTrading';
import { OutliersAdminPage } from '@pages/outliers/OutliersAdminPage';
import { OutlierContractAdminPage } from '@pages/outliers/OutliersContractAdminPage';
import PrivatePage from '@pages/PrivatePage';
import { ProductPage } from '@pages/ProductPage';
import ProductsPage from '@pages/ProductsPage';
import { ProfilePage } from '@pages/ProfilePage';
import { SettlementsPage } from '@pages/SettlementsPage';
import { TasksPage } from '@pages/TasksPage';
import { LiveMidPricesPage } from '@pages/trader-marks/LiveMidPrices';
import { TradingAdminPage } from '@pages/TradingAdminPage';
import OrganizationPage from '@pages/OrganizationPage';
import { OrganizationsPage } from '@pages/OrganizationsPage';
import UserPage from '@pages/UserPage';
import { Users } from '@pages/UsersPage';
import { Freemium } from '@features/admin/freemium/Freemium';
import { FreemiumNews } from '@features/dashboard/freemium/NewsDashboard/FreemiumNews';
import { FreemiumRouteGuard } from '@auth/FreemiumRouteGaurd';
import { MarketsDashboard } from '@features/dashboard/freemium/markets/MarketsDashboard';
import { config } from '@services/context';
import { checkIsOutliersAdmin } from '@shared/utils/permissions';

// LAZY LOADING PAGES
const AdminPage = lazy(() => import('@pages/AdminPage'));
const DashboardPage = lazy(() => import('@pages/DashboardPage'));

export const AppRoutes = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path="/cot" element={<CotPage />} />

        <Route
          element={
            <Auth0ProviderWithNavigate>
              <PrivatePage />
            </Auth0ProviderWithNavigate>
          }
        >
          <Route element={<AuthenticationGuard component={Outlet} />}>
            <Route path="freemium-news" element={<FreemiumNews />} handle={{ crumbName: 'Freemium News' }} />
            <Route path="/manage-account" element={<ProfilePage />} handle={{ crumbName: 'Manage Account' }} />
            <Route element={<FreemiumRouteGuard component={Outlet} />}>
              <Route
                path="admin"
                element={
                  <AdminPage>
                    <Outlet />
                  </AdminPage>
                }
              >
                <Route path="" element={<Navigate to="/admin/trading" />} />
                <Route path="settlements" element={<SettlementsPage />} handle={{ crumbName: 'Admin Settlements' }} />
                <Route path="alerts" element={<AlertsPage />} handle={{ crumbName: 'Admin Alerts' }} />
                <Route path="trading">
                  <Route path="" element={<TradingAdminPage />} handle={{ crumbName: 'Trading Admin' }} />
                  <Route path=":activeTab" element={<TradingAdminPage />} handle={{ crumbName: 'Trading Admin' }} />
                  <Route path="orders/:id" element={<OrderPage />} handle={{ crumbName: 'Order details' }} />
                </Route>
                <Route path="tasks">
                  <Route path="" element={<TasksPage />} handle={{ crumbName: 'Tasks - Admin' }} />
                </Route>
                <Route path="endpoint-measures">
                  <Route path="" element={<EndpointMeasures />} handle={{ crumbName: 'Endpoint Measures' }} />
                </Route>
                <Route path="live-websockets" element={<LiveWebsocketsPage />} handle={{ crumbName: 'Live Websockets' }} />
                <Route path="freemium">
                  <Route path="" element={<Freemium />} handle={{ crumbName: 'Freemium' }} />
                  {/*
                  TODO: Uncomment this when the updateFreemiumData endpoint supports the_officials category
                  <Route path="officials-update" element={<TheOfficialsUpdatePage />} handle={{ crumbName: 'Freemium Officials Update Page' }} />
                  */}
                </Route>
              </Route>

              <Route
                path="admin"
                element={
                  <AdminPage canAccess={checkIsOutliersAdmin}>
                    <Outlet />
                  </AdminPage>
                }
              >
                <Route path="outliers">
                  <Route path="" element={<OutliersAdminPage />} handle={{ crumbName: 'Outliers - Admin' }} />
                  <Route path=":contractSymbol" element={<OutlierContractAdminPage />} handle={{ crumbName: 'Outliers Contract - Admin' }} />
                </Route>
              </Route>

              <Route path="dashboard">
                <Route index path="" element={<DashboardPage />} handle={{ crumbName: 'Dashboard' }} />
                <Route path=":dashboardId" element={<DashboardPage />} handle={{ crumbName: 'Dashboard' }} />
              </Route>
              <Route path="/manage-account" element={<ProfilePage />} handle={{ crumbName: 'Manage Account' }} />
              <Route path="/" element={<Navigate to="dashboard" />} />

              <Route path="/ocl-trading">
                <Route path="" element={<OCLTrading />} handle={{ crumbName: 'OCL Trading' }} />
                <Route path=":activeTab" element={<OCLTrading />} handle={{ crumbName: 'OCL Trading' }} />
              </Route>
              <Route path="users">
                <Route path="" element={<Users />} handle={{ crumbName: 'Users - Admin' }} />
                <Route path=":id" element={<UserPage />} handle={{ crumbName: 'Manage User' }} />
              </Route>
              <Route path="organizations">
                <Route path="" element={<OrganizationsPage />} handle={{ crumbName: 'Organizations - Admin' }} />
                <Route path=":id" element={<OrganizationPage />} handle={{ crumbName: 'Manage Organization' }} />
              </Route>
              <Route path="trader-marks">
                <Route path="" element={<LiveMidPricesPage />} handle={{ crumbName: 'Trader Marks' }} />
              </Route>
              <Route path="freemium">
                <Route path="" element={<Freemium />} handle={{ crumbName: 'Freemium' }} />
                <Route path="news" element={<FreemiumNews />} handle={{ crumbName: 'Freemium News' }} />
                {config.isDev && <Route path="markets" element={<MarketsDashboard />} handle={{ crumbName: 'Freemium Markets' }} />}
              </Route>
              <Route path="reference">
                <Route path="products">
                  <Route path="" element={<ProductsPage />} handle={{ crumbName: 'Products' }} />
                  <Route path=":symbol" element={<ProductPage />} handle={{ crumbName: 'Product details' }} />
                </Route>
                <Route path="calendar">
                  <Route path="" element={<CalendarPage />} handle={{ crumbName: 'Calendar' }} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} handle={{ crumbName: 'Not Found' }} />
          </Route>
        </Route>
      </Route>
    )
  );

  return <RouterProvider router={router} />;
};
